import React from 'react'
import { intl } from 'lang/intl'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { get } from 'lodash-es'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'

import { TtButton } from '@23/react'

import {
  Button,
  AnimateComponent,
  ContentLoaderComponent,
  ModalComponent,
  IconComponent,
} from '../../../../components'
import {
  ModeContentSwitchComponent,
  CompactListComponent,
  CompactEditableListComponent,
  ExpandedListComponent,
} from '../index'

import './SpeakersFeatureComponent.scss'

const crudInlineContainerProps = {
  addToEventCheckbox: true,
  addCancelButton: true,
  sizeCenteredModalIcon: 'small',
}

const crudModalProps = {
  isSendInviteButtonVisible: true,
}

const speakersTriggerButton = (
  <TtButton
    className="speakers-trigger-button"
    variant="outline"
    color="secondary"
    pill
  >
    <FormattedMessage defaultMessage="Invite your first speaker" />
  </TtButton>
)

const getEmptyStateText = (listType, styles, viewOnly) => {
  let text = (
    <FormattedMessage defaultMessage="No speakers have been added to this event yet." />
  )

  if (listType !== 'compact') {
    if (viewOnly) {
      text = (
        <span className="invite-link">
          <FormattedMessage defaultMessage="No speakers invited." />
        </span>
      )
    } else {
      text = speakersTriggerButton
    }
  }

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className="empty-list-text" styles={get(styles, 'textStyles')}>
      {text}
    </div>
  )
}

const getWrapperClickHandler = (listType, openCrudModal, viewOnly, animate) => {
  if (viewOnly || listType === 'compact' || animate) {
    return {}
  }

  return { onClick: () => openCrudModal('new') }
}

const getContentLoader = (opt, animate = false) => {
  const { namespace, listType, openCrudModal, viewOnly, styles } = opt
  const className = classnames(`empty-list list-type-${listType}`, {
    'is-loaded': !animate,
    disabled: viewOnly,
  })

  return (
    <div
      className={className}
      {...getWrapperClickHandler(listType, openCrudModal, viewOnly, animate)}
    >
      <ContentLoaderComponent
        animate={animate}
        set={
          listType !== 'expanded'
            ? 'square-avatar-list-two-items'
            : 'user-details'
        }
        uniquekey={namespace}
        noBackground={get(styles, 'loaderStyles.background') === 'transparent'}
        wrapperStyle={get(styles, 'loaderStyles')}
      />
      {!animate && getEmptyStateText(listType, styles, viewOnly)}
    </div>
  )
}

const getAddButton = (opt) => {
  const { listType, openCrudModal, viewOnly, isAllowedAddingSpeaker } = opt

  return (
    <div className="add-speaker-wrapper">
      <Button
        color="transparent"
        iconConfig={{ name: 'plus', size: [10, 10], color: '1EC95B' }}
        {...getWrapperClickHandler(listType, openCrudModal, viewOnly)}
        disabled={!isAllowedAddingSpeaker}
        {...(!isAllowedAddingSpeaker && {
          tooltip: intl.formatMessage({
            defaultMessage: 'Maximum number of speakers reached',
          }),
        })}
      >
        <FormattedMessage defaultMessage="Add Speaker" />
      </Button>
    </div>
  )
}

const getSpeakers = (opt) => {
  const {
    speakers,
    listType,
    openCrudModal,
    sendInvite,
    sendInviteSpeakerId,
    viewOnly,
    reorder,
    renderAddSpeakerButton,
    styles,
    size,
    showDemoteButton,
    showChyronControls,
  } = opt

  let speakersList = null

  if (listType === 'compact') {
    speakersList = (
      <CompactListComponent
        speakers={speakers}
        viewOnly={viewOnly}
        style={get(styles, 'textStyles')}
        size={size}
        showDemoteButton={showDemoteButton}
        showChyronControls={showChyronControls}
      />
    )
  } else if (
    listType === 'compact-editable' ||
    listType === 'compact-editable-inline'
  ) {
    speakersList = (
      <CompactEditableListComponent
        speakers={speakers}
        onEditClick={openCrudModal}
        viewOnly={viewOnly}
        reorder={reorder}
      />
    )
  } else if (listType === 'expanded') {
    speakersList = (
      <ExpandedListComponent
        speakers={speakers}
        openCrudModal={openCrudModal}
        onSendClick={sendInvite}
        sendInviteSpeakerId={sendInviteSpeakerId}
        viewOnly={viewOnly}
      />
    )
  }

  return (
    <>
      {speakersList}
      {renderAddSpeakerButton && getAddButton(opt)}
    </>
  )
}

const getCrudContainer = (opt, customProps = {}) => (
  <ModeContentSwitchComponent
    crudMode={opt.crudMode}
    onSubmit={opt.save}
    openCrudModal={opt.openCrudModal}
    closeCrudModal={opt.closeCrudModal}
    isVisible={opt.isCrudModalVisible}
    okButtonState={opt.crudButtonState}
    addedSpeaker={opt.addedSpeaker}
    sendInvite={opt.sendInvite}
    crudSpeakerId={opt.crudSpeakerId}
    sendInviteSpeakerId={opt.sendInviteSpeakerId}
    isCrudSpeakerInviteSent={opt.isCrudSpeakerInviteSent}
    {...customProps}
  />
)

const getCrudModal = (opt) => (
  <ModalComponent
    destroyOnClose
    visible={opt.isCrudModalVisible}
    onCancel={opt.closeCrudModal}
    wrapClassName="crud-modal-component"
    maskClosable={false}
  >
    {getCrudContainer(opt, crudModalProps)}
  </ModalComponent>
)

const getCrudInline = (opt) => (
  <div className="crud-inline-wrapper">
    <AnimateComponent duration={200} transitionName="animation-fly-right">
      {opt.isCrudModalVisible && (
        <div className="crud-inline-inner">
          {getCrudContainer(opt, crudInlineContainerProps)}
          <IconComponent
            name="cancel"
            size={[12, 12]}
            className="close-inline"
            color="CCC"
            onClick={opt.closeCrudModal}
          />
        </div>
      )}
    </AnimateComponent>
  </div>
)

const SpeakersFeatureComponent = (props) => {
  let content = null
  const { loading, speakers, listType, renderIfEmpty } = props

  if (loading) {
    content = getContentLoader(props, true)
  } else if (!loading && speakers.length === 0) {
    if (renderIfEmpty) {
      content = getContentLoader(props, false)
    }
  } else {
    content = getSpeakers(props)
  }

  return (
    <div className={`speakers-feature-component ${listType}`}>
      <div className="speakers-feature-inner">{content}</div>
      {(listType === 'compact-editable' || listType === 'expanded') &&
        getCrudModal(props)}
      {listType === 'compact-editable-inline' && getCrudInline(props)}
    </div>
  )
}

SpeakersFeatureComponent.propTypes = {
  listType: PropTypes.oneOf([
    'compact',
    'compact-editable',
    'expanded',
    'compact-editable-inline',
  ]),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  crudButtonState: PropTypes.oneOf(['idle', 'loading', 'success', 'failure']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  crudMode: PropTypes.oneOf([
    'new',
    'speaker-added',
    'invitation-sent',
    'edit',
    'delete',
  ]),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  liveId: PropTypes.string,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  namespace: PropTypes.string,
  speakers: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  sendInviteSpeakerId: PropTypes.string,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isCrudModalVisible: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  openCrudModal: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  closeCrudModal: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  save: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  sendInvite: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  styles: PropTypes.object,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  viewOnly: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isCrudSpeakerInviteSent: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  addedSpeaker: PropTypes.object,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  crudSpeakerId: PropTypes.string,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  reorder: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isAllowedAddingSpeaker: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  size: PropTypes.oneOf(['small', 'large']),
  renderIfEmpty: PropTypes.bool,
  // False positive.
  // eslint-disable-next-line react/no-unused-prop-types
  showDemoteButton: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  showChyronControls: PropTypes.bool,
}

SpeakersFeatureComponent.defaultProps = {
  speakers: [],
  listType: 'compact',
  size: 'small',
  renderIfEmpty: true,
  showDemoteButton: false,
  showChyronControls: false,
}

const mapStateProps = (state, ownProps) => {
  const { getIsAllowedAddingSpeaker, getIsCrudSpeakerInviteSent } =
    ownProps.selectors

  return {
    isAllowedAddingSpeaker: getIsAllowedAddingSpeaker(state),
    isCrudSpeakerInviteSent: getIsCrudSpeakerInviteSent(state),
  }
}

export default connect(mapStateProps)(SpeakersFeatureComponent)
